/**
 * Exceptions — the auditor's wording, versioned; management responds to the
 * issued version (client side).
 *   T6-B9  "Revise wording" makes a new version (history kept); leads
 *          withdraw; Section 4 reads "Waiting on management's response to
 *          CC6.3 v2" with "Message client", each reason linking to its row; a
 *          Fail cell's "Draft exception" lands here prefilled.
 *   T6-H3  the standing Section 4 state is static text, not an alert.
 *   Medium restricted actions show disabled with the reason; the new
 *          exception form sits in its own card.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Card, EmptyState, Field, Input, Textarea } from '@aigovops/design-system';
import { ActionDialog } from '../../components/today/shared';
import { firmApi, type FirmException, type PbcRequest, type Section4 } from '../../lib/firm-api';
import { fmtDate } from '../../lib/format';
import { Chip, GuardedButton, StateBadge, TabHeading, useAct, useDraft, useFirm, useFocusLater, useLoad, Loading } from './firm-kit';

const LEAD_ONLY = 'A manager or partner issues and withdraws exceptions.';

export function ExceptionsTab() {
  const { slug, eng, writable, lockId } = useFirm();
  const l = useLoad(() => firmApi.exceptions(slug, eng), [slug, eng]);
  const s4 = useLoad(() => firmApi.section4(slug, eng), [slug, eng]);
  const pbc = useLoad(() => firmApi.pbc(slug, eng), [slug, eng]);
  const reload = () => { l.reload(); s4.reload(); pbc.reload(); };
  return (
    <div className="gp-firm-stack">
      <TabHeading id="exc-h">Exceptions</TabHeading>
      <Loading l={s4}>{(d) => <Section4Line s4={d} requests={pbc.data?.requests ?? []} onChanged={reload} />}</Loading>
      <Loading l={l}>{(d) => d.exceptions.length === 0 ? <EmptyState title="No exceptions" action={<a href="?tab=tests">Draft one from a failed result</a>} /> : (
        <ul className="gp-firm-list" aria-labelledby="exc-h">
          {d.exceptions.map((x) => <ExceptionRow key={x.exception_id} x={x} onChanged={reload} />)}
        </ul>
      )}</Loading>
      {writable ? <NewException onCreated={reload} /> : <p className="gp-firm-muted" aria-describedby={lockId ?? undefined}>New exceptions are closed.</p>}
    </div>
  );
}

function Section4Line({ s4, requests, onChanged }: { s4: Section4; requests: PbcRequest[]; onChanged: () => void }) {
  const { slug, eng, writable } = useFirm();
  const act = useAct(onChanged);
  if (!s4.locked) return <p className="gp-firm-muted"><span className="gp-firm-ok">Section 4 consistent</span> · every response answers the issued version</p>;
  const items = s4.items ?? s4.reasons.map((reason) => ({ exception_id: '', control_ref: reason.split(':')[0] ?? '', issued_version: 0, response_version: null, kind: 'response_stale' as const, reason }));
  return (
    <div className="gp-firm-callout" id="section4-state">
      <p className="gp-firm-callout-title">Section 4 locked</p>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <ul className="gp-firm-list">
        {items.map((i) => {
          const title = `Management response to ${i.control_ref} v${i.issued_version}`;
          const open = requests.find((r) => r.title === title && r.status !== 'accepted' && r.status !== 'withdrawn');
          return (
            <li key={`${i.exception_id}-${i.kind}`} className="gp-firm-actions">
              {i.exception_id ? <a href={`#exc-${i.exception_id}`}>{i.kind === 'response_stale' ? `Waiting on management’s response to ${i.control_ref} v${i.issued_version}` : i.reason}</a> : <span>{i.reason}</span>}
              {writable && i.exception_id ? (
                <GuardedButton compact busy={act.busy} onClick={() => void act.run(
                  () => (open ? firmApi.pbcMessage(slug, eng, open.request_id, `Please update management’s response to ${i.control_ref} v${i.issued_version}.`) : firmApi.createPbc(slug, eng, { title, control_ref: i.control_ref, detail: `The response on file answers v${i.response_version ?? '—'}. Respond to v${i.issued_version}.` })),
                  open ? `Message sent on ${open.ref}` : 'Client asked to update the response',
                )}>Message client</GuardedButton>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ExceptionRow({ x, onChanged }: { x: FirmException; onChanged: () => void }) {
  const { slug, eng, lead, writable } = useFirm();
  const act = useAct(onChanged);
  const focus = useFocusLater();
  const latest = x.versions[x.versions.length - 1]!;
  const [revising, setRevising] = useState(false);
  const [wording, setWording] = useDraft(`exc-rev-${x.exception_id}`, '');
  const [tried, setTried] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const rowId = `exc-${x.exception_id}`;
  const status = x.status === 'issued' ? (x.issued_version === x.latest_version ? `Issued v${x.issued_version}` : `Issued v${x.issued_version} · v${x.latest_version} draft`) : undefined;
  const revise = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (wording.trim().length < 10) return;
    if (await act.call('reviseException', [slug, eng, x.exception_id, wording.trim()], `${x.control_ref} v${x.latest_version + 1} saved`)) { setRevising(false); setWording(''); setTried(false); focus(rowId); }
  };
  return (
    <li className="gp-firm-row" id={rowId} tabIndex={-1}>
      <div className="gp-firm-row-head">
        <h3 className="gp-firm-row-title">{x.control_ref}</h3>
        <StateBadge state={x.status} label={status} />
      </div>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <p>{latest.wording}</p>
      {x.versions.length > 1 ? (
        <details className="gp-firm-history">
          <summary>Version history ({x.versions.length})</summary>
          <ol>
            {[...x.versions].reverse().map((v) => <li key={v.version}><strong>v{v.version}</strong>{v.issued_at ? ` · issued ${fmtDate(v.issued_at)}` : ' · not issued'}: {v.wording}</li>)}
          </ol>
        </details>
      ) : null}
      {x.response ? <p className="gp-firm-muted">Management (answers v{x.response.exception_version}, {x.response.unaudited_label.toLowerCase()}): {x.response.body}</p> : x.status === 'issued' ? <p className="gp-firm-muted">Waiting on management’s response</p> : null}
      {writable && x.status !== 'withdrawn' ? (
        revising ? (
          <form className="gp-firm-form" onSubmit={revise} noValidate>
            <Field id={`rev-${x.exception_id}`} label={`New wording (v${x.latest_version + 1})`} error={tried && wording.trim().length < 10 ? 'Write the wording (at least 10 characters).' : null}>
              <Textarea rows={3} value={wording} onChange={(e) => setWording(e.target.value)} />
            </Field>
            <div className="gp-firm-actions">
              <GuardedButton type="submit" busy={act.busy}>Save v{x.latest_version + 1}</GuardedButton>
              <GuardedButton variant="ghost" onClick={() => { setRevising(false); setTried(false); }}>Cancel</GuardedButton>
            </div>
          </form>
        ) : (
          <div className="gp-firm-actions">
            <GuardedButton compact onClick={() => { setRevising(true); if (!wording) setWording(latest.wording); focus(`rev-${x.exception_id}`); }}>Revise wording</GuardedButton>
            {x.issued_version !== x.latest_version ? (
              <GuardedButton compact busy={act.busy} blocked={!lead} reason={LEAD_ONLY} onClick={() => void act.call('issueException', [slug, eng, x.exception_id], `${x.control_ref} v${x.latest_version} issued to the client`)}>Issue v{x.latest_version} to client</GuardedButton>
            ) : null}
            <GuardedButton compact variant="ghost" blocked={!lead} reason={lead ? undefined : LEAD_ONLY} onClick={() => setWithdrawing(true)}>Withdraw…</GuardedButton>
          </div>
        )
      ) : null}
      <ActionDialog open={withdrawing} title={`Withdraw the ${x.control_ref} exception?`} submitLabel="Withdraw" danger busy={act.busy} error={act.error}
        onClose={() => setWithdrawing(false)}
        onSubmit={async () => { if (await act.call('withdrawException', [slug, eng, x.exception_id], `${x.control_ref} exception withdrawn`)) { setWithdrawing(false); focus(rowId); } }}>
        <p>{x.status === 'issued' ? 'The client stops seeing it in Section 4. The history stays in the working papers.' : 'The draft is kept in the history but won’t be issued.'}</p>
      </ActionDialog>
      {x.status === 'withdrawn' ? <Chip>Withdrawn</Chip> : null}
    </li>
  );
}

function NewException({ onCreated }: { onCreated: () => void }) {
  const { slug, eng } = useFirm();
  const [params, setParams] = useSearchParams();
  const act = useAct(onCreated);
  const focus = useFocusLater();
  const [control, setControl] = useDraft('x-control', params.get('draft_control') ?? '');
  const [wording, setWording] = useDraft('x-wording', params.get('draft_wording') ?? '');
  const [testId] = useState(params.get('draft_test'));
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (!params.get('draft_control')) return;
    focus('x-wording');
    setParams((p) => { p.delete('draft_control'); p.delete('draft_wording'); p.delete('draft_test'); return p; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!control.trim() || wording.trim().length < 10) return;
    if (await act.call('createException', [slug, eng, { control_ref: control.trim(), wording: wording.trim(), test_id: testId }], `${control.trim()} exception drafted (firm only)`)) {
      setControl(''); setWording(''); setTried(false);
      focus('exc-h');
    }
  };
  return (
    <Card title="New exception">
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <form className="gp-firm-form" onSubmit={submit} noValidate>
        <Field id="x-control" label="Control" error={tried && !control.trim() ? 'Name the control.' : null}><Input value={control} onChange={(e) => setControl(e.target.value)} /></Field>
        <Field id="x-wording" label="Exception wording" hint={testId ? 'From a failed test result · edit before saving' : 'Drafts stay in the firm tier until issued.'} error={tried && wording.trim().length < 10 ? 'Write the wording (at least 10 characters).' : null}>
          <Textarea rows={3} value={wording} onChange={(e) => setWording(e.target.value)} />
        </Field>
        <div className="gp-firm-actions"><GuardedButton type="submit" busy={act.busy}>Save draft</GuardedButton></div>
      </form>
    </Card>
  );
}
